/**
 * SecretPassageSystem — manages hidden walls that can be revealed by
 * pushing against them 3 times in quick succession.
 *
 * Secret passages are interior walls selected at maze generation time.
 * They have a subtly different texture (hairline crack) that observant
 * players can notice. Pushing against one 3 times within the cadence
 * interval opens it permanently, turning it into a walkable PATH cell.
 *
 * Appears from level 3+.
 */

import { CellType, Events, TEX_SECRET_WALL } from '../utils/constants.js';
import {
  TILE_SIZE,
  SECRET_PASSAGE_MIN_LEVEL,
  SECRET_PASSAGE_FRACTION,
  SECRET_PASSAGE_MIN_COUNT,
  SECRET_PASSAGE_MAX_COUNT,
  SECRET_PASSAGE_PUSH_COUNT,
  SECRET_PASSAGE_PUSH_INTERVAL,
  AUDIO_SECRET_PASSAGE_OPEN,
  AUDIO_SECRET_PASSAGE_HINT,
  COLOR_SECRET_WALL_CRACK,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';
import MazeGenerator from './MazeGenerator.js';

export default class SecretPassageSystem {
  /**
   * @param {Phaser.Scene} scene     - The GameScene instance.
   * @param {Phaser.Events.EventEmitter} eventBus - Shared event bus.
   * @param {number[][]} grid        - The maze grid (expanded).
   * @param {number} level           - Current game level.
   * @param {{ col: number, row: number }} startPos
   * @param {{ col: number, row: number }} exitPos
   * @param {number} [seed]          - Optional seed for deterministic placement.
   */
  constructor(scene, eventBus, grid, level, startPos, exitPos, seed) {
    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {Phaser.Events.EventEmitter} */
    this.eventBus = eventBus;

    /** @type {number[][]} */
    this.grid = grid;

    /** @type {number} */
    this.level = level;

    /**
     * Map of secret passage walls. Key: "col,row", Value: { pushCount, lastPushTime, sprite, opened }
     * @type {Map<string, object>}
     */
    this._passages = new Map();

    /**
     * Set of opened passage keys ("col,row") for quick lookup.
     * @type {Set<string>}
     */
    this._openedPassages = new Set();

    /** @type {boolean} */
    this._destroyed = false;

    // Seeded RNG (simple LCG)
    this._rngState = seed ?? (Date.now() ^ 0xbaadf00d);

    if (level >= SECRET_PASSAGE_MIN_LEVEL) {
      this._selectSecretWalls(startPos, exitPos);
      this._renderSecretWalls();
    }
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Called when the player tries to move into a wall cell.
   * Checks if the wall is a secret passage and tracks push count.
   *
   * @param {number} wallCol - Grid column of the wall being pushed.
   * @param {number} wallRow - Grid row of the wall being pushed.
   * @returns {boolean} True if the passage was opened by this push.
   */
  onPushWall(wallCol, wallRow) {
    if (this._destroyed) return false;

    const key = `${wallCol},${wallRow}`;
    const passage = this._passages.get(key);
    if (!passage || passage.opened) return false;

    const now = Date.now();

    // Check cadence — reset if too slow
    if (passage.pushCount > 0 && now - passage.lastPushTime > SECRET_PASSAGE_PUSH_INTERVAL) {
      passage.pushCount = 0;
    }

    passage.pushCount++;
    passage.lastPushTime = now;

    // Play hint sound on first/second push
    if (passage.pushCount < SECRET_PASSAGE_PUSH_COUNT) {
      // Subtle vibration on the wall sprite
      this._vibrateSecretWall(passage);

      try {
        this.scene.sound.play(AUDIO_SECRET_PASSAGE_HINT, { volume: getSfxVol(0.2) });
      } catch (_) { /* audio fallback */ }

      return false;
    }

    // Third push — open the passage!
    this._openPassage(wallCol, wallRow, passage);
    return true;
  }

  /**
   * Check if a grid cell is a secret passage (opened or not).
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  isSecretPassage(col, row) {
    return this._passages.has(`${col},${row}`);
  }

  /**
   * Check if a secret passage has been opened.
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  isOpened(col, row) {
    return this._openedPassages.has(`${col},${row}`);
  }

  /**
   * Serialize for save state.
   * @returns {{ passages: Array<{ col: number, row: number, opened: boolean, pushCount: number }> }}
   */
  serialize() {
    const passages = [];
    for (const [key, data] of this._passages.entries()) {
      const [col, row] = key.split(',').map(Number);
      passages.push({
        col,
        row,
        opened: data.opened,
        pushCount: data.opened ? SECRET_PASSAGE_PUSH_COUNT : 0,
      });
    }
    return { passages };
  }

  /**
   * Restore secret passages from save data.
   * @param {{ passages: Array<{ col: number, row: number, opened: boolean }> }} saveData
   */
  restore(saveData) {
    if (!saveData || !saveData.passages) return;

    // Clear current passages
    this._destroySprites();
    this._passages.clear();
    this._openedPassages.clear();

    for (const p of saveData.passages) {
      const key = `${p.col},${p.row}`;
      this._passages.set(key, {
        pushCount: 0,
        lastPushTime: 0,
        sprite: null,
        opened: p.opened,
      });

      if (p.opened) {
        this._openedPassages.add(key);
        // Ensure grid is PATH
        this.grid[p.row][p.col] = CellType.PATH;
      }
    }

    // Re-render non-opened secret walls
    this._renderSecretWalls();
  }

  /**
   * Destroy all sprites and clean up.
   */
  destroy() {
    this._destroyed = true;
    this._destroySprites();
    this._passages.clear();
    this._openedPassages.clear();
    this.scene = null;
    this.eventBus = null;
    this.grid = null;
  }

  // ---------------------------------------------------------------
  // Private — selection
  // ---------------------------------------------------------------

  /**
   * Simple LCG pseudo-random number generator.
   * @private
   * @returns {number} Value in [0, 1).
   */
  _nextRandom() {
    this._rngState = (this._rngState * 1664525 + 1013904223) & 0x7fffffff;
    return this._rngState / 0x80000000;
  }

  /**
   * Select which interior walls will become secret passages.
   * A secret wall must:
   *   1. Be an interior wall (not on boundary)
   *   2. Have exactly two PATH neighbors on opposite sides (horizontal or vertical)
   *      — this ensures it's a wall between two corridors/rooms
   *   3. Not be adjacent to start or exit
   *
   * @private
   * @param {{ col: number, row: number }} startPos
   * @param {{ col: number, row: number }} exitPos
   */
  _selectSecretWalls(startPos, exitPos) {
    const gridH = this.grid.length;
    const gridW = this.grid[0].length;

    // Collect all candidate interior walls
    const candidates = [];

    // Exclude cells near start and exit
    const excluded = new Set();
    for (const pos of [startPos, exitPos]) {
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          excluded.add(`${pos.col + dx},${pos.row + dy}`);
        }
      }
    }

    for (let row = 2; row < gridH - 2; row++) {
      for (let col = 2; col < gridW - 2; col++) {
        if (this.grid[row][col] !== CellType.WALL) continue;
        if (excluded.has(`${col},${row}`)) continue;

        // Check for PATH on opposite sides (horizontal: left+right, vertical: up+down)
        const leftPath = col > 0 && this.grid[row][col - 1] === CellType.PATH;
        const rightPath = col < gridW - 1 && this.grid[row][col + 1] === CellType.PATH;
        const upPath = row > 0 && this.grid[row - 1][col] === CellType.PATH;
        const downPath = row < gridH - 1 && this.grid[row + 1][col] === CellType.PATH;

        const isHorizontalBridge = leftPath && rightPath;
        const isVerticalBridge = upPath && downPath;

        if (isHorizontalBridge || isVerticalBridge) {
          candidates.push({ col, row });
        }
      }
    }

    if (candidates.length === 0) return;

    // Calculate count: fraction of interior walls, clamped to min/max
    const interiorWallCount = candidates.length;
    let targetCount = Math.round(interiorWallCount * SECRET_PASSAGE_FRACTION);
    targetCount = Math.max(SECRET_PASSAGE_MIN_COUNT, Math.min(SECRET_PASSAGE_MAX_COUNT, targetCount));
    targetCount = Math.min(targetCount, candidates.length);

    // Shuffle candidates using seeded RNG
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(this._nextRandom() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    // Pick the first targetCount
    for (let i = 0; i < targetCount; i++) {
      const { col, row } = candidates[i];
      const key = `${col},${row}`;
      this._passages.set(key, {
        pushCount: 0,
        lastPushTime: 0,
        sprite: null,
        opened: false,
      });
    }
  }

  // ---------------------------------------------------------------
  // Private — rendering
  // ---------------------------------------------------------------

  /**
   * Render secret wall sprites on top of the existing wall sprites.
   * Uses TEX_SECRET_WALL texture (a wall with a subtle crack).
   * @private
   */
  _renderSecretWalls() {
    if (!this.scene) return;

    for (const [key, data] of this._passages.entries()) {
      if (data.opened) continue; // Already opened — no sprite needed
      if (data.sprite) continue; // Already rendered

      const [colStr, rowStr] = key.split(',');
      const col = parseInt(colStr, 10);
      const row = parseInt(rowStr, 10);

      const x = col * TILE_SIZE + TILE_SIZE / 2;
      const y = row * TILE_SIZE + TILE_SIZE / 2;

      const sprite = this.scene.add.sprite(x, y, TEX_SECRET_WALL);
      sprite.setOrigin(0.5);
      sprite.setDepth(3); // Slightly above normal walls (2)
      data.sprite = sprite;
    }
  }

  /**
   * Destroy all secret wall sprites.
   * @private
   */
  _destroySprites() {
    for (const [, data] of this._passages.entries()) {
      if (data.sprite && data.sprite.active) {
        data.sprite.destroy();
      }
      data.sprite = null;
    }
  }

  // ---------------------------------------------------------------
  // Private — opening
  // ---------------------------------------------------------------

  /**
   * Open a secret passage: update grid, remove wall sprite, play effects.
   * @private
   * @param {number} col
   * @param {number} row
   * @param {object} passage
   */
  _openPassage(col, row, passage) {
    passage.opened = true;
    const key = `${col},${row}`;
    this._openedPassages.add(key);

    // Update maze grid: wall -> path
    this.grid[row][col] = CellType.PATH;

    // Remove the original wall sprite from the wall physics group
    this._removeWallSprite(col, row);

    // Remove the secret wall overlay sprite
    if (passage.sprite) {
      passage.sprite.destroy();
      passage.sprite = null;
    }

    // Play open sound
    try {
      this.scene.sound.play(AUDIO_SECRET_PASSAGE_OPEN, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    // Visual effect: brief dust/stone particles
    this._playOpenEffect(col, row);

    // Emit event
    this.eventBus.emit(Events.SECRET_PASSAGE_DISCOVERED, { col, row });
  }

  /**
   * Remove the wall sprite from the scene's wallGroup at the given position.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _removeWallSprite(col, row) {
    if (!this.scene.wallGroup) return;
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;
    const children = this.scene.wallGroup.getChildren();
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i];
      if (Math.abs(child.x - x) < 1 && Math.abs(child.y - y) < 1) {
        child.destroy();
        break;
      }
    }
  }

  /**
   * Subtle vibration on the secret wall sprite when pushed.
   * @private
   * @param {object} passage
   */
  _vibrateSecretWall(passage) {
    if (!passage.sprite || !this.scene) return;
    const sprite = passage.sprite;
    const origX = sprite.x;

    this.scene.tweens.add({
      targets: sprite,
      x: origX + 1.5,
      duration: 40,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (sprite.active) sprite.x = origX;
      },
    });
  }

  /**
   * Play a brief stone-dust particle effect when a passage opens.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _playOpenEffect(col, row) {
    if (!this.scene) return;

    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;

    // Brief white flash
    const flash = this.scene.add.graphics();
    flash.setDepth(210);
    flash.fillStyle(0xffffff, 0.6);
    flash.fillCircle(x, y, TILE_SIZE * 0.8);

    this.scene.tweens.add({
      targets: { alpha: 0.6 },
      alpha: 0,
      duration: 400,
      ease: 'Quad.easeOut',
      onUpdate: (tween) => {
        const a = tween.getValue();
        flash.clear();
        flash.fillStyle(0xffffff, a);
        flash.fillCircle(x, y, TILE_SIZE * 0.8);
      },
      onComplete: () => {
        flash.destroy();
      },
    });
  }
}
